import React, { useCallback, useMemo } from 'react';
import { View } from 'react-native';
import TouchableBounce from 'utils/packages/TouchableBounce';
import FadingContainer from 'design-system/FadingContainer';
import Spacer from 'design-system/Spacer';
import Text from 'design-system/Text';
import { rem } from 'design-system/values';
import { muteErrorAndResult, selectIsScrambled, useFetch, useFetchOld, } from 'hooks';
import { formatAmount, formatDateForItem } from 'utils/formatting';
import IconTransfer from 'design-system/icons/IconTransfer';
import IconCircle from 'design-system/icons/IconCircle';
import { Avatar, AvatarWrapper } from 'design-system/avatar';
import useColors from 'hooks/useColors';
import createStyleSheets from 'utils/createStyleSheets';
import useStyles from 'hooks/useStyles';
import { useAppSelector } from 'store/hooks';
import StatusIcon from 'design-system/StatusIcon';
import LogoBankAccount, { CIRCLE_BORDER_SIZE, CIRCLE_SIZE, } from 'design-system/LogoBankAccount';
import { IconEmma } from 'design-system/icons';
import RadioButton from 'design-system/RadioButton';
import { useRemindedRequest, useThankedPayment } from '../hooks';
import BlueBellIcon from './BlueBellIcon';
import RedHeartIcon from './RedHeartIcon';
import { mapActivitySide, mapActivityStatus } from '../util/utils';
import { sendPaymentThanks } from '../actions';
import { sendReminders } from '../actions/payments';
const PaymentItem = ({ payment, hideActions, isSelectable, isSelected, isMultiSelect, disabled, spendingGroup, }) => {
    const colors = useColors();
    const styles = useStyles(styleSet);
    const { counterparty, amount, currency, beneficiaryDetails } = payment;
    const isCurrentUsersRequest = payment.type === 'PAYMENT_REQUEST' &&
        payment.side === 'PAYEE' &&
        payment.counterparty === null;
    const fullName = useMemo(() => {
        if (isCurrentUsersRequest) {
            return 'Payment request';
        }
        if (counterparty) {
            if ('nickname' in counterparty)
                return counterparty.nickname;
            const { firstName, lastName, phoneNumber } = counterparty;
            return !firstName && !lastName ? phoneNumber : `${firstName} ${lastName}`;
        }
        if (beneficiaryDetails) {
            return beneficiaryDetails.name;
        }
        return 'Unknown';
    }, [beneficiaryDetails, counterparty, isCurrentUsersRequest]);
    const counterpartyHandle = useMemo(() => payment.counterparty && 'handle' in payment.counterparty
        ? payment.counterparty.handle
        : undefined, [payment.counterparty]);
    const isScrambled = useAppSelector(selectIsScrambled);
    const amountText = useMemo(() => {
        const amt = formatAmount(amount, currency, undefined, isScrambled);
        if (payment.type === 'INTERNAL_TRANSFER') {
            return amt;
        }
        return mapActivitySide(payment, {
            payee: `+${amt}`,
            payer: `${amt}`,
        });
    }, [currency, amount, isScrambled, payment]);
    const amountStyle = useMemo(() => {
        const amtStyle = mapActivitySide(payment, {
            payee: styles.textAmountIncome,
            payer: styles.textAmountSpend,
        });
        return mapActivityStatus(payment, {
            notActioned: amtStyle,
            pending: amtStyle,
            completed: amtStyle,
            default: styles.textAmountCancelled,
        });
    }, [
        payment,
        styles.textAmountCancelled,
        styles.textAmountIncome,
        styles.textAmountSpend,
    ]);
    const amountColor = useMemo(() => {
        let result = {};
        const { side, status } = payment;
        switch (status) {
            case 'NOT_YET_ACTIONED':
            case 'COMPLETED':
                if (side === 'PAYEE') {
                    result = { Blue: true };
                    return result;
                }
                return {};
            case 'PENDING':
                result = { Gray: true };
                return result;
            default:
                return {};
        }
    }, [payment]);
    const hasThankAction = useMemo(() => payment.side === 'PAYEE' &&
        (payment.type === 'DIRECT_PAYMENT' ||
            payment.type === 'PAYMENT_REQUEST') &&
        payment.status === 'COMPLETED', [payment.side, payment.type, payment.status]);
    const hasRemindAction = useMemo(() => payment.side === 'PAYEE' &&
        payment.type === 'PAYMENT_REQUEST' &&
        payment.status === 'NOT_YET_ACTIONED', [payment.side, payment.type, payment.status]);
    const actionVisible = useMemo(() => !hideActions &&
        ((hasThankAction && payment.canThank) ||
            (hasRemindAction && payment.canSendReminder)), [
        hideActions,
        hasThankAction,
        payment.canThank,
        payment.canSendReminder,
        hasRemindAction,
    ]);
    const [sendThanks, sendingThanks] = useFetch();
    const [, , sendingReminder, fetchReminder] = useFetchOld(muteErrorAndResult);
    const [, setThanked] = useThankedPayment();
    const [, setReminded] = useRemindedRequest();
    const onPressThanks = () => {
        sendThanks(sendPaymentThanks(payment.paymentId))
            .then(() => setThanked({ ...payment, canThank: false }))
            .catch(() => { });
    };
    const onPressRemind = useCallback(async () => {
        if (!counterpartyHandle || !payment.counterparty)
            return;
        const body = {};
        if (spendingGroup) {
            body.groupSettlements = [
                {
                    groupId: spendingGroup.id,
                    payerUserHandle: counterpartyHandle,
                },
            ];
        }
        else {
            body.paymentRequests = [
                {
                    paymentRequestId: payment.paymentRequestId,
                    payerUserHandle: counterpartyHandle,
                },
            ];
        }
        const res = await fetchReminder(sendReminders(body));
        if (!res)
            return;
        setReminded([payment.counterparty]);
    }, [
        counterpartyHandle,
        fetchReminder,
        payment.counterparty,
        payment.paymentRequestId,
        setReminded,
        spendingGroup,
    ]);
    const onPressAction = (sendingThanks || !payment.canThank ? undefined : onPressThanks) ??
        (sendingReminder || !payment.canSendReminder ? undefined : onPressRemind);
    const getIcon = () => {
        if (hasThankAction)
            return <RedHeartIcon isFetching={sendingThanks}/>;
        if (hasRemindAction)
            return <BlueBellIcon isFetching={sendingReminder}/>;
        return null;
    };
    const renderLogo = () => {
        if (payment.type === 'INTERNAL_TRANSFER') {
            return (<IconCircle color={colors.background.brandLight} width={44} height={44}>
          <IconTransfer colorKey="brand"/>
        </IconCircle>);
        }
        if (isCurrentUsersRequest) {
            return (<AvatarWrapper color={colors.background.brandLight} size={rem(44)} borderWidth={1}>
          <IconEmma colorKey="brand"/>
        </AvatarWrapper>);
        }
        if (beneficiaryDetails) {
            return <LogoBankAccount size="R44" data={{}}/>;
        }
        return (<Avatar size={CIRCLE_SIZE} name={(counterparty && 'nickname' in counterparty
                ? counterparty.nickname
                : counterparty?.firstName) || '?'} lastName={counterparty && 'nickname' in counterparty
                ? undefined
                : counterparty?.lastName} imageUrl={counterparty && 'imageUrl' in counterparty
                ? counterparty.imageUrl
                : undefined} color={colors.background.brandLight} fontColor={colors.text.textOnBrand} borderWidth={CIRCLE_BORDER_SIZE} medium/>);
    };
    return (<View style={styles.row}>
      {isSelectable ? (<>
          <RadioButton type={isMultiSelect ? 'multiple' : 'single'} isChecked={isSelected} disabled={disabled}/>
          <Spacer.W16 />
        </>) : null}
      {renderLogo()}
      <View style={styles.textContainer}>
        <Text Text-16 numberOfLines={1}>
          {payment.type === 'INTERNAL_TRANSFER'
            ? `Transfer to ${payment.payeeAccountDetails?.accountName}`
            : fullName}
        </Text>
        {spendingGroup ? (<Text TextThin-14 Secondary numberOfLines={1}>
            in {spendingGroup.name}
          </Text>) : ((payment.type !== 'INTERNAL_TRANSFER' ||
            (payment.type === 'INTERNAL_TRANSFER' &&
                payment.status !== 'COMPLETED')) && (<View style={styles.texStatus}>
              <View style={styles.iconStatus}>
                {mapActivityStatus(payment, {
                notActioned: <StatusIcon state="in-progress"/>,
                pending: <StatusIcon state="in-progress"/>,
                completed: mapActivitySide(payment, {
                    payee: <StatusIcon state="received"/>,
                    payer: <StatusIcon state="sent"/>,
                }),
                default: <StatusIcon state="cancelled-gray"/>,
            })}
              </View>

              {mapActivityStatus(payment, {
                notActioned: (<Text TextThin-14 color={colors.text.statusInformational}>
                    Pending
                  </Text>),
                pending: (<Text TextThin-14 color={colors.text.statusInformational}>
                    Processing
                  </Text>),
                completed: mapActivitySide(payment, {
                    payee: (<Text TextThin-14 color={colors.text.statusPositive}>
                      Received
                    </Text>),
                    payer: (<Text TextThin-14 color={colors.text.statusNegative}>
                      Paid
                    </Text>),
                }),
                default: (<Text TextThin-14 color={colors.text.statusOffline}>
                    Cancelled
                  </Text>),
            })}
            </View>))}
      </View>
      <View style={{ alignItems: 'flex-end' }}>
        {actionVisible ? (<FadingContainer value={actionVisible ? 1 : 0} delay={2000} pointerEvents={actionVisible ? 'auto' : 'none'}>
            <TouchableBounce style={styles.thanksButton} onPress={onPressAction}>
              {getIcon()}
              <Spacer w={5}/>
              <Text Numbers-16 numberOfLines={1} style={amountStyle} {...amountColor}>
                {amountText}
              </Text>
            </TouchableBounce>
          </FadingContainer>) : (<Text Numbers-16 numberOfLines={1} style={amountStyle} {...amountColor}>
            {amountText}
          </Text>)}
        {!spendingGroup && (<Text TextThin-14 Gray numberOfLines={1}>
            {formatDateForItem(payment.date)}
          </Text>)}
      </View>
    </View>);
};
const styleSet = createStyleSheets((colors) => ({
    row: {
        height: rem(76),
        paddingHorizontal: rem(16),
        flexDirection: 'row',
        alignItems: 'center',
    },
    textContainer: {
        flex: 1,
        marginHorizontal: rem(16),
        justifyContent: 'center',
    },
    iconStatus: {
        marginRight: rem(8),
    },
    texStatus: {
        flexDirection: 'row',
        alignItems: 'center',
    },
    textAmountIncome: {
        color: colors.text.income,
    },
    textAmountSpend: {},
    textAmountCancelled: {
        textDecorationLine: 'line-through',
    },
    thanksButton: {
        marginRight: -rem(8),
        paddingHorizontal: rem(8),
        paddingVertical: rem(4),
        borderRadius: rem(16),
        marginBottom: rem(4),
        borderWidth: 1,
        borderColor: colors.borders.divider,
        flexDirection: 'row',
        alignItems: 'center',
    },
}));
export default PaymentItem;
